import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaUser,
  FaMoneyBill,
  FaTimesCircle,
  FaFileInvoice,
  FaPrint,
} from "react-icons/fa";
import { FiArrowLeft, FiCheckCircle, FiClock, FiFileText, FiPrinter } from "react-icons/fi";

const InvoiceDetail = () => {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const encodedinvoiceNumber = encodeURIComponent(invoiceNumber);
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/invoice/${encodedinvoiceNumber}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!res.ok) throw new Error("Gagal fetch data invoice");
        const result = await res.json();
        setInvoiceData(result.data);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceNumber]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-12 text-center text-gray-500 font-semibold">
        Data invoice tidak ditemukan.
      </div>
    );
  }

  const {
    number,
    created_at,
    due_date,
    paid_date,
    amount = 0,
    tax = 0,
    created_by,
    paid_by,
    cancel_by,
    status,
    desc,
    evidence,
  } = invoice;

  const formatDate = (date) =>
    date ? new Date(date).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-";

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inventory/invoice")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Detail Invoice #{number}
            </h1>
            <p className="text-xs text-gray-500">
              Informasi lengkap tagihan, tanggal jatuh tempo, dan bukti pembayaran
            </p>
          </div>
        </div>

        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
        >
          <FaPrint className="w-4 h-4 text-emerald-600" />
          <span>Cetak Invoice</span>
        </button>
      </motion.div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* LEFT CARD */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5"
        >
          <div className="flex items-center justify-between border-b border-gray-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                <FaFileInvoice className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Nomor Invoice
                </span>
                <h2 className="text-lg font-extrabold font-mono text-gray-900">{number}</h2>
              </div>
            </div>

            <span
              className={`px-3 py-1 text-xs font-extrabold rounded-full border capitalize ${
                status?.toLowerCase() === "paid"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : status?.toLowerCase() === "pending"
                  ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-gray-50 text-gray-700 border-gray-200"
              }`}
            >
              {status}
            </span>
          </div>

          <div className="text-xs text-gray-600 space-y-2.5">
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-blue-500 w-4 h-4" />
              <span>
                <strong>Tanggal Dibuat:</strong> {formatDate(created_at)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-amber-500 w-4 h-4" />
              <span>
                <strong>Jatuh Tempo:</strong> {formatDate(due_date)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-emerald-500 w-4 h-4" />
              <span>
                <strong>Tanggal Dibayar:</strong> {formatDate(paid_date)}
              </span>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-100 text-xs text-gray-700 space-y-2.5">
            <div className="flex items-center gap-2">
              <FaUser className="text-blue-500 w-3.5 h-3.5" />
              <span>
                <strong>Dibuat oleh:</strong> {created_by || "Admin"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FaMoneyBill className="text-emerald-600 w-3.5 h-3.5" />
              <span>
                <strong>Dibayar oleh:</strong> {paid_by || "-"}
              </span>
            </div>
            {cancel_by && (
              <div className="flex items-center gap-2 text-rose-600">
                <FaTimesCircle className="w-3.5 h-3.5" />
                <span>
                  <strong>Dibatalkan oleh:</strong> {cancel_by}
                </span>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-700 space-y-1">
            <span className="font-bold text-gray-400 uppercase text-[10px]">Catatan / Deskripsi</span>
            <p className="p-3 bg-gray-50 rounded-2xl border border-gray-100 text-gray-800 font-semibold">
              {desc || "Tidak ada deskripsi"}
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 text-xs space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span className="font-bold font-mono">
                Rp {((amount || 0) - (tax || 0)).toLocaleString("id-ID")}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Pajak (PPN)</span>
              <span className="font-bold font-mono">Rp {(tax || 0).toLocaleString("id-ID")}</span>
            </div>
            <div className="flex justify-between text-base font-extrabold text-emerald-800 pt-2 border-t border-gray-100">
              <span>Total Tagihan</span>
              <span className="font-mono">Rp {(amount || 0).toLocaleString("id-ID")}</span>
            </div>
          </div>
        </motion.div>

        {/* RIGHT CARD */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col justify-between"
        >
          <h3 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3">
            Lampiran Bukti Pembayaran
          </h3>

          <div className="py-4">
            {evidence ? (
              <img
                src={evidence}
                alt="Bukti pembayaran"
                className="w-full max-h-[380px] object-contain border border-gray-200 rounded-2xl shadow-sm hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="text-gray-400 font-semibold text-xs border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center bg-gray-50/50">
                Belum ada bukti pembayaran yang diunggah
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvoiceDetail;